// src/pages/Campaigns/SurveyGenie/SurveyTextAnalysis.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    LinearProgress,
    IconButton,
    Tooltip,
    Divider,
    List,
    ListItem,
    ListItemText,
    Avatar,
    useTheme
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    SentimentSatisfiedAlt as PositiveIcon,
    SentimentDissatisfied as NegativeIcon,
    SentimentNeutral as NeutralIcon,
    Psychology as MixedIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    FormatQuote as QuoteIcon,
    Cloud as CloudIcon,
    Category as ThemeIcon,
    Analytics as AnalyticsIcon,
    ArrowBack as BackIcon
} from '@mui/icons-material';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
    Area,
    AreaChart
} from 'recharts';
import axiosInstance from '../../../../utils/axiosInstance';

// Sentiment colors for charts
const SENTIMENT_COLORS = {
    POSITIVE: '#4caf50',
    NEGATIVE: '#f44336',
    NEUTRAL: '#9e9e9e',
    MIXED: '#ff9800'
};

// Helper function to normalize sentiment keys (handles both uppercase and lowercase)
const normalizeSentimentKey = (key) => {
    if (!key) return 'NEUTRAL';
    return key.toUpperCase();
};

// Helper function to get sentiment value from data (case-insensitive)
const getSentimentValue = (sentimentData, sentimentType) => {
    if (!sentimentData) return 0;
    
    // Try uppercase first
    if (sentimentData[sentimentType] !== undefined) {
        return sentimentData[sentimentType];
    }
    
    // Try lowercase
    const lowerKey = sentimentType.toLowerCase();
    if (sentimentData[lowerKey] !== undefined) {
        return sentimentData[lowerKey];
    }
    
    return 0;
};

// Helper function to normalize sentiment breakdown to uppercase keys
const normalizeSentimentBreakdown = (breakdown) => {
    if (!breakdown) return { POSITIVE: 0, NEGATIVE: 0, NEUTRAL: 0, MIXED: 0 };
    
    const normalized = {
        POSITIVE: 0,
        NEGATIVE: 0,
        NEUTRAL: 0,
        MIXED: 0
    };
    
    Object.entries(breakdown).forEach(([key, value]) => {
        const upperKey = normalizeSentimentKey(key);
        normalized[upperKey] = value || 0;
    });
    
    return normalized;
};

// Word Cloud Component
const WordCloud = ({ words, onWordClick }) => {
    const theme = useTheme();
    
    if (!words || words.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                No keywords extracted yet
            </Box>
        );
    }

    const maxCount = Math.max(...words.map(w => w.count));
    const minCount = Math.min(...words.map(w => w.count));
    
    const getFontSize = (count) => {
        const minSize = 12;
        const maxSize = 48;
        if (maxCount === minCount) return (minSize + maxSize) / 2;
        return minSize + ((count - minCount) / (maxCount - minCount)) * (maxSize - minSize);
    };

    const getColor = (index) => {
        const colors = [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.success.main,
            theme.palette.info.main,
            theme.palette.warning.main,
            '#8884d8',
            '#82ca9d',
            '#ffc658'
        ];
        return colors[index % colors.length];
    };

    return (
        <Box 
            sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                p: 2,
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200
            }}
        >
            {words.slice(0, 50).map((word, index) => (
                <Tooltip key={word.word} title={`Count: ${word.count}`}>
                    <Typography
                        component="span"
                        onClick={() => onWordClick && onWordClick(word.word)}
                        sx={{
                            fontSize: getFontSize(word.count),
                            fontWeight: word.count > (maxCount * 0.5) ? 600 : 400,
                            color: getColor(index),
                            cursor: 'pointer',
                            transition: 'transform 0.2s, opacity 0.2s',
                            '&:hover': {
                                transform: 'scale(1.1)',
                                opacity: 0.8
                            }
                        }}
                    >
                        {word.word}
                    </Typography>
                </Tooltip>
            ))}
        </Box>
    );
};

// Sentiment Badge Component
const SentimentBadge = ({ sentiment, score, size = 'medium' }) => {
    const getConfig = () => {
        const normalizedSentiment = normalizeSentimentKey(sentiment);
        switch (normalizedSentiment) {
            case 'POSITIVE':
                return { 
                    icon: <PositiveIcon />, 
                    color: 'success', 
                    label: 'Positive',
                    bgColor: '#e8f5e9'
                };
            case 'NEGATIVE':
                return { 
                    icon: <NegativeIcon />, 
                    color: 'error', 
                    label: 'Negative',
                    bgColor: '#ffebee'
                };
            case 'NEUTRAL':
                return { 
                    icon: <NeutralIcon />, 
                    color: 'default', 
                    label: 'Neutral',
                    bgColor: '#f5f5f5'
                };
            case 'MIXED':
                return { 
                    icon: <MixedIcon />, 
                    color: 'warning', 
                    label: 'Mixed',
                    bgColor: '#fff3e0'
                };
            default:
                return { 
                    icon: <NeutralIcon />, 
                    color: 'default', 
                    label: 'Unknown',
                    bgColor: '#f5f5f5'
                };
        }
    };

    const config = getConfig();

    return (
        <Chip
            icon={config.icon}
            label={score !== undefined ? `${config.label} (${(score * 100).toFixed(0)}%)` : config.label}
            color={config.color}
            size={size}
            sx={{ 
                fontWeight: 500,
                '& .MuiChip-icon': { fontSize: size === 'small' ? 16 : 20 }
            }}
        />
    );
};

// Quote Card Component
const QuoteCard = ({ quote, sentiment }) => {
    const theme = useTheme();
    
    const getSentimentColor = () => {
        const normalizedSentiment = normalizeSentimentKey(sentiment);
        switch (normalizedSentiment) {
            case 'POSITIVE': return theme.palette.success.light;
            case 'NEGATIVE': return theme.palette.error.light;
            case 'NEUTRAL': return theme.palette.grey[300];
            case 'MIXED': return theme.palette.warning.light;
            default: return theme.palette.grey[300];
        }
    };

    return (
        <Card 
            variant="outlined" 
            sx={{ 
                mb: 2,
                borderLeft: `4px solid ${getSentimentColor()}`,
                '&:hover': { boxShadow: 2 }
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <QuoteIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                fontStyle: 'italic',
                                mb: 1,
                                lineHeight: 1.6
                            }}
                        >
                            "{quote.text}"
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                {quote.question_text && `Q: ${quote.question_text.substring(0, 50)}...`}
                            </Typography>
                            <SentimentBadge sentiment={sentiment} score={quote.sentiment_score} size="small" />
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// Main Component
const SurveyTextAnalysis = () => {
    const { chatbotId, surveyId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    
    // State
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedQuestion, setSelectedQuestion] = useState('all');
    
    // Data state
    const [analysisStatus, setAnalysisStatus] = useState(null);
    const [sentimentData, setSentimentData] = useState(null);
    const [keywordData, setKeywordData] = useState(null);
    const [trendData, setTrendData] = useState(null);
    const [quotesData, setQuotesData] = useState(null);
    const [demographicsData, setDemographicsData] = useState(null);

    // Fetch analysis status
    const fetchAnalysisStatus = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/surveys/analysis/status', {
                params: { survey_id: surveyId }
            });
            if (response.data.success) {
                setAnalysisStatus(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching analysis status:', err);
        }
    }, [surveyId]);

    // Fetch sentiment analysis
    const fetchSentimentAnalysis = useCallback(async () => {
        try {
            const params = { survey_id: surveyId };
            if (selectedQuestion !== 'all') {
                params.question_id = selectedQuestion;
            }
            
            const response = await axiosInstance.get('/api/surveys/analysis/sentiment', { params });
            console.log('Sentiment data received:', response.data); // Debug log
            
            if (response.data.success) {
                // Normalize the sentiment breakdown
                const data = response.data.data;
                if (data && data.sentiment_breakdown) {
                    data.sentiment_breakdown = normalizeSentimentBreakdown(data.sentiment_breakdown);
                }
                setSentimentData(data);
            }
        } catch (err) {
            console.error('Error fetching sentiment analysis:', err);
        }
    }, [surveyId, selectedQuestion]);

    // Fetch keyword analysis
    const fetchKeywordAnalysis = useCallback(async () => {
        try {
            const params = { survey_id: surveyId };
            if (selectedQuestion !== 'all') {
                params.question_id = selectedQuestion;
            }
            
            const response = await axiosInstance.get('/api/surveys/analysis/keywords', { params });
            console.log('Keyword data received:', response.data); // Debug log
            
            if (response.data.success) {
                setKeywordData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching keyword analysis:', err);
        }
    }, [surveyId, selectedQuestion]);

    // Fetch sentiment trend
    const fetchSentimentTrend = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/surveys/analysis/sentiment-trend', {
                params: { survey_id: surveyId, days: 30 }
            });
            if (response.data.success) {
                setTrendData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching sentiment trend:', err);
        }
    }, [surveyId]);

    // Fetch quotes
    const fetchQuotes = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/surveys/analysis/quotes', {
                params: { survey_id: surveyId, max_quotes: 5 }
            });
            console.log('Quotes data received:', response.data); // Debug log
            
            if (response.data.success) {
                setQuotesData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching quotes:', err);
        }
    }, [surveyId]);

    // Fetch demographics breakdown
    const fetchDemographics = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/surveys/analysis/sentiment-demographics', {
                params: { survey_id: surveyId, segment_by: 'device_type' }
            });
            if (response.data.success) {
                setDemographicsData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching demographics:', err);
        }
    }, [surveyId]);

    // Trigger analysis
    const triggerAnalysis = async (forceReanalyze = false) => {
        setAnalyzing(true);
        setError(null);
        
        try {
            const response = await axiosInstance.post('/api/surveys/analysis/trigger', {
                survey_id: parseInt(surveyId),
                force_reanalyze: forceReanalyze
            });
            
            if (response.data.success) {
                // Wait a bit for analysis to complete
                setTimeout(async () => {
                    await Promise.all([
                        fetchAnalysisStatus(),
                        fetchSentimentAnalysis(),
                        fetchKeywordAnalysis(),
                        fetchSentimentTrend(),
                        fetchQuotes(),
                        fetchDemographics()
                    ]);
                }, 2000);
            } else {
                setError(response.data.message || 'Analysis failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to trigger analysis');
        } finally {
            setAnalyzing(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    fetchAnalysisStatus(),
                    fetchSentimentAnalysis(),
                    fetchKeywordAnalysis(),
                    fetchSentimentTrend(),
                    fetchQuotes(),
                    fetchDemographics()
                ]);
            } catch (err) {
                setError('Failed to load analysis data');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [fetchAnalysisStatus, fetchSentimentAnalysis, fetchKeywordAnalysis, fetchSentimentTrend, fetchQuotes, fetchDemographics]);

    // Refetch when question filter changes
    useEffect(() => {
        if (!loading) {
            fetchSentimentAnalysis();
            fetchKeywordAnalysis();
        }
    }, [selectedQuestion, fetchSentimentAnalysis, fetchKeywordAnalysis, loading]);

    // Export report
    const exportReport = () => {
        const reportData = {
            survey_id: surveyId,
            generated_at: new Date().toISOString(),
            sentiment: sentimentData,
            keywords: keywordData,
            trend: trendData,
            quotes: quotesData
        };
        
        const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `text-analysis-report-${surveyId}-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Prepare chart data with normalized sentiment breakdown
    const pieChartData = React.useMemo(() => {
        if (!sentimentData?.sentiment_breakdown) return [];
        
        const normalized = normalizeSentimentBreakdown(sentimentData.sentiment_breakdown);
        
        return Object.entries(normalized)
            .filter(([_, value]) => value > 0) // Only include non-zero values
            .map(([name, value]) => ({
                name: name.charAt(0) + name.slice(1).toLowerCase(), // "Positive", "Negative", etc.
                value,
                color: SENTIMENT_COLORS[name]
            }));
    }, [sentimentData]);

    const themeChartData = keywordData?.themes ? 
        Object.entries(keywordData.themes)
            .map(([name, data]) => ({
                name,
                count: data.count,
                keywords: data.keywords?.length || 0
            }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 8) : [];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Normalize sentiment breakdown for display
    const normalizedBreakdown = sentimentData?.sentiment_breakdown 
        ? normalizeSentimentBreakdown(sentimentData.sentiment_breakdown)
        : { POSITIVE: 0, NEGATIVE: 0, NEUTRAL: 0, MIXED: 0 };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)}>
                        <BackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            Text & Sentiment Analysis
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            AI-powered analysis of open-ended responses
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={exportReport}
                        disabled={!sentimentData}
                    >
                        Export Report
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        onClick={() => triggerAnalysis(true)}
                        disabled={analyzing}
                    >
                        {analyzing ? 'Analyzing...' : 'Run Analysis'}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Analysis Status */}
            {analysisStatus && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                                Analysis Progress
                            </Typography>
                            <Typography variant="body2">
                                {analysisStatus.analyzed_answers} of {analysisStatus.total_text_answers} responses analyzed
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 200 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={analysisStatus.analysis_percentage} 
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>
                            <Typography variant="body2" fontWeight={600}>
                                {analysisStatus.analysis_percentage}%
                            </Typography>
                        </Box>
                    </Box>
                    {analysisStatus.last_analyzed && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Last analyzed: {new Date(analysisStatus.last_analyzed).toLocaleString()}
                        </Typography>
                    )}
                </Paper>
            )}

            {/* Question Filter */}
            {analysisStatus?.questions?.length > 1 && (
                <FormControl size="small" sx={{ mb: 3, minWidth: 300 }}>
                    <InputLabel>Filter by Question</InputLabel>
                    <Select
                        value={selectedQuestion}
                        label="Filter by Question"
                        onChange={(e) => setSelectedQuestion(e.target.value)}
                    >
                        <MenuItem value="all">All Text Questions</MenuItem>
                        {analysisStatus.questions.map(q => (
                            <MenuItem key={q.id} value={q.id}>
                                {q.question_text.substring(0, 60)}...
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
                    <Tab icon={<AnalyticsIcon />} label="Overview" iconPosition="start" />
                    <Tab icon={<CloudIcon />} label="Keywords" iconPosition="start" />
                    <Tab icon={<ThemeIcon />} label="Themes" iconPosition="start" />
                    <Tab icon={<QuoteIcon />} label="Quotes" iconPosition="start" />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
                <Grid container spacing={3}>
                    {/* Sentiment Overview Cards */}
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            {['POSITIVE', 'NEGATIVE', 'NEUTRAL', 'MIXED'].map(sentiment => {
                                const count = normalizedBreakdown[sentiment] || 0;
                                const total = sentimentData?.total_analyzed || 0;
                                const percentage = total > 0 ? (count / total * 100).toFixed(1) : '0';
                                
                                return (
                                    <Grid item xs={6} md={3} key={sentiment}>
                                        <Card>
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    {sentiment === 'POSITIVE' && <PositiveIcon sx={{ color: SENTIMENT_COLORS.POSITIVE }} />}
                                                    {sentiment === 'NEGATIVE' && <NegativeIcon sx={{ color: SENTIMENT_COLORS.NEGATIVE }} />}
                                                    {sentiment === 'NEUTRAL' && <NeutralIcon sx={{ color: SENTIMENT_COLORS.NEUTRAL }} />}
                                                    {sentiment === 'MIXED' && <MixedIcon sx={{ color: SENTIMENT_COLORS.MIXED }} />}
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        {sentiment.charAt(0) + sentiment.slice(1).toLowerCase()}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="h4" fontWeight={600} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                                                    {count}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {percentage}%
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Grid>

                    {/* Sentiment Distribution Pie Chart */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Sentiment Distribution
                            </Typography>
                            {pieChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={pieChartData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            paddingAngle={2}
                                            dataKey="value"
                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {pieChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                    <Typography color="text.secondary">No data available</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Sentiment Trend */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Sentiment Trend (Last 30 Days)
                            </Typography>
                            {trendData?.trend?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={trendData.trend}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="date" 
                                            tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Legend />
                                        <Area type="monotone" dataKey="positive" stackId="1" stroke={SENTIMENT_COLORS.POSITIVE} fill={SENTIMENT_COLORS.POSITIVE} fillOpacity={0.6} />
                                        <Area type="monotone" dataKey="neutral" stackId="1" stroke={SENTIMENT_COLORS.NEUTRAL} fill={SENTIMENT_COLORS.NEUTRAL} fillOpacity={0.6} />
                                        <Area type="monotone" dataKey="mixed" stackId="1" stroke={SENTIMENT_COLORS.MIXED} fill={SENTIMENT_COLORS.MIXED} fillOpacity={0.6} />
                                        <Area type="monotone" dataKey="negative" stackId="1" stroke={SENTIMENT_COLORS.NEGATIVE} fill={SENTIMENT_COLORS.NEGATIVE} fillOpacity={0.6} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                    <Typography color="text.secondary">No trend data available</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Average Scores */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Average Scores
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={6} md={3}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Sentiment Score
                                        </Typography>
                                        <Typography variant="h5" fontWeight={600}>
                                            {sentimentData?.average_score?.toFixed(2) || sentimentData?.avg_sentiment_score?.toFixed(2) || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Scale: -1 (negative) to +1 (positive)
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Subjectivity
                                        </Typography>
                                        <Typography variant="h5" fontWeight={600}>
                                            {sentimentData?.average_subjectivity?.toFixed(2) || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Scale: 0 (objective) to 1 (subjective)
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Total Analyzed
                                        </Typography>
                                        <Typography variant="h5" fontWeight={600}>
                                            {sentimentData?.total_analyzed || sentimentData?.total_responses || 0}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Text responses
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Keywords Found
                                        </Typography>
                                        <Typography variant="h5" fontWeight={600}>
                                            {keywordData?.keywords?.length || keywordData?.top_keywords?.length || 0}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Unique keywords
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {activeTab === 1 && (
                <Grid container spacing={3}>
                    {/* Word Cloud */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Word Cloud
                            </Typography>
                            <WordCloud 
                                words={
                                    keywordData?.keywords?.map(item => 
                                        Array.isArray(item) 
                                            ? { word: item[0], count: item[1] }
                                            : { word: item.word, count: item.count }
                                    ) || []
                                }
                                onWordClick={(word) => console.log('Clicked:', word)}
                            />
                        </Paper>
                    </Grid>

                    {/* Top Keywords List */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Top Keywords
                            </Typography>
                            <List>
                                {(keywordData?.keywords || []).slice(0, 15).map((item, index) => {
                                    const [word, count] = Array.isArray(item) ? item : [item.word, item.count];
                                    return (
                                        <ListItem key={word} divider={index < 14}>
                                            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2, width: 32, height: 32 }}>
                                                {index + 1}
                                            </Avatar>
                                            <ListItemText 
                                                primary={word}
                                                secondary={`${count} occurrences`}
                                            />
                                            <Chip label={count} size="small" variant="outlined" />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Paper>
                    </Grid>

                    {/* Phrases */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Common Phrases
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {(keywordData?.phrases || []).slice(0, 20).map((item) => {
                                    const [phrase, count] = Array.isArray(item) ? item : [item.phrase, item.count];
                                    return (
                                        <Chip 
                                            key={phrase}
                                            label={`${phrase} (${count})`}
                                            variant="outlined"
                                            sx={{ m: 0.5 }}
                                        />
                                    );
                                }) || (
                                    <Typography color="text.secondary">No phrases extracted</Typography>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {activeTab === 2 && (
                <Grid container spacing={3}>
                    {/* Theme Distribution */}
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Theme Distribution
                            </Typography>
                            {themeChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={400}>
                                    <BarChart data={themeChartData} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis dataKey="name" type="category" width={150} />
                                        <RechartsTooltip />
                                        <Bar dataKey="count" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                                    <Typography color="text.secondary">No themes identified</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Theme Details */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Theme Keywords
                            </Typography>
                            {keywordData?.themes && Object.entries(keywordData.themes)
                                .sort((a, b) => b[1].count - a[1].count)
                                .slice(0, 5)
                                .map(([themeName, themeData]) => (
                                    <Box key={themeName} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {themeName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            {themeData.keywords?.slice(0, 5).map(kw => (
                                                <Chip 
                                                    key={kw.word}
                                                    label={kw.word}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                        <Divider sx={{ mt: 1.5 }} />
                                    </Box>
                                ))
                            }
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {activeTab === 3 && (
                <Grid container spacing={3}>
                    {['POSITIVE', 'NEGATIVE', 'NEUTRAL', 'MIXED'].map((sentiment) => {
                        // Get quotes for this sentiment (handle both uppercase and lowercase keys)
                        const quotes = quotesData?.[sentiment] || quotesData?.[sentiment.toLowerCase()] || [];
                        
                        return (
                            <Grid item xs={12} md={6} key={sentiment}>
                                <Paper sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        {sentiment === 'POSITIVE' && <PositiveIcon sx={{ color: SENTIMENT_COLORS.POSITIVE }} />}
                                        {sentiment === 'NEGATIVE' && <NegativeIcon sx={{ color: SENTIMENT_COLORS.NEGATIVE }} />}
                                        {sentiment === 'NEUTRAL' && <NeutralIcon sx={{ color: SENTIMENT_COLORS.NEUTRAL }} />}
                                        {sentiment === 'MIXED' && <MixedIcon sx={{ color: SENTIMENT_COLORS.MIXED }} />}
                                        <Typography variant="h6">
                                            {sentiment.charAt(0) + sentiment.slice(1).toLowerCase()} Feedback
                                        </Typography>
                                    </Box>
                                    {quotes.length > 0 ? (
                                        quotes.map((quote, idx) => (
                                            <QuoteCard key={idx} quote={quote} sentiment={sentiment} />
                                        ))
                                    ) : (
                                        <Typography color="text.secondary">
                                            No {sentiment.toLowerCase()} quotes found
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

export default SurveyTextAnalysis;